import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { PaginationV2, PriceList, SelectedPriceListColumns } from 'src/types';
import { Product } from 'src/types/product';
import * as thunks from './thunks';
import {
  initialQuery,
  queryParamsInterface
} from 'src/views/price-list/constants';

export type ComponentPBData={
     product_ids:string[];
    open:boolean;
    bundle_id?:number;
}

type ProductState = {
  products: Product[];
  productsMeta: PaginationV2;
  priceList: PriceList[];
  productPriceList: any[]; // band aid for ts error
  priceListMeta: PaginationV2;
  isPriceListLoading: boolean;

  priceListParams: queryParamsInterface;
  priceListDate: string;
  productTableColumns: SelectedPriceListColumns;
  componentProductsState:ComponentPBData;
};

const initialState: ProductState = {
  products: [],
  productsMeta: {},
  priceList: [],
  productPriceList: [],
  priceListMeta: {},
  priceListParams: initialQuery,
  isPriceListLoading: false,
  priceListDate: '',
  productTableColumns: {
    name: true,
    branch: true,
    quantity: true,
    srp: true,
    installment: true,
    promo_price: true,
    dealer_price: true,
    gross: true,
    margin: true,
    out_of_stock_at: true
  },
  componentProductsState:{
    product_ids:[],
    open:false,
    bundle_id:undefined
  }

};

const slice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    // band aid any for ts error
    setProductPriceList: (state, { payload }: PayloadAction<any[]>) => {
      state.productPriceList = payload;
    },
    setPriceListParams: (
      state,
      { payload }: PayloadAction<queryParamsInterface>
    ) => {
      state.priceListParams = payload;
    },

    updateProductViaId: (state, { payload }: PayloadAction<Product>) => {
      if (payload.id) {
        const index = state.products.findIndex((x) => x.id);
        if (index > -1) {
          state.products[index] = payload;
        }
      }
    },
    updatePriceListPriceViaIndex: (
      state,
      { payload }: PayloadAction<{ promo_price: number; index: number }>
    ) => {
      state.priceList[payload.index].promo_price = payload.promo_price;
    },

    removeProduct: (state, { payload }: PayloadAction<number>) => {
      const clonedProducts = cloneDeep(state.products);
      const index = state?.products?.findIndex((x) => x?.id === payload);
      if (index > -1) {
        clonedProducts.splice(index, 1);
      }
      state.products = clonedProducts;
    },

    onSetProductTableColumns: (state, { payload }) => {
      state.productTableColumns = payload;
    },

    setPoweredBuildsProductComponentIds: (state, { payload }) => {
      state.componentProductsState = payload;
    },
  },

  extraReducers(builder) {
    builder.addCase(thunks.getProductsThunk.fulfilled, (state, { payload }) => {
      state.products = payload?.originalData?.data;
      state.productsMeta = payload?.originalData?.meta;
    });

    builder.addCase(thunks.getPriceListThunk.pending, (state) => {
      state.isPriceListLoading = true;
    });

    builder.addCase(thunks.getPriceListThunk.rejected, (state) => {
      state.isPriceListLoading = false;
    });

    builder.addCase(
      thunks.getPriceListThunk.fulfilled,
      (state, { payload }) => {
        const newData = payload?.originalData?.data || [];
        const meta = payload?.originalData?.meta || {};
        const currentPage = payload?.originalData?.meta?.current_page || 0;

        if (currentPage > 1) {
          state.productPriceList = [...state.productPriceList, ...newData];
        } else {
          state.productPriceList = newData;
        }

        state.priceListMeta = meta;
        state.isPriceListLoading = false;
      }
    );

    builder.addCase(thunks.getIncomingPriceListThunk.pending, (state) => {
      state.isPriceListLoading = true;
    });

    builder.addCase(thunks.getIncomingPriceListThunk.rejected, (state) => {
      state.isPriceListLoading = false;
    });

    builder.addCase(
      thunks.getIncomingPriceListThunk.fulfilled,
      (state, { payload }) => {
        const newData = payload?.originalData?.data || [];
        const meta = payload?.originalData?.meta || {};
        const currentPage = payload?.originalData?.meta?.current_page || 0;

        if (currentPage > 1) {
          state.productPriceList = [...state.productPriceList, ...newData];
        } else {
          state.productPriceList = newData;
        }

        state.priceListMeta = meta;
        state.isPriceListLoading = false;
      }
    );

    builder.addCase(thunks.getPoweredBuildsPriceListThunk.pending, (state) => {
      state.isPriceListLoading = true;
    });

    builder.addCase(thunks.getPoweredBuildsPriceListThunk.rejected, (state) => {
      state.isPriceListLoading = false;
    });

    builder.addCase(
      thunks.getPoweredBuildsPriceListThunk.fulfilled,
      (state, { payload }) => {
        const newData = payload?.originalData?.data || [];
        const meta = payload?.originalData?.meta || {};
        const currentPage = payload?.originalData?.meta?.current_page || 0;

        if (currentPage > 1) {
          state.productPriceList = [...state.productPriceList, ...newData];
        } else {
          state.productPriceList = newData;
        }

        state.priceListMeta = meta;
        state.isPriceListLoading = false;
      }
    );

    // update product from pricelist
    builder.addCase(thunks.updateProductsThunk.pending, (state) => {
      state.isPriceListLoading = true;
    });

    builder.addCase(thunks.updateProductsThunk.rejected, (state) => {
      state.isPriceListLoading = false;
    });

    builder.addCase(thunks.updateProductsThunk.fulfilled, (state) => {
      state.isPriceListLoading = false;
    });
  }
});

export const reducer = slice.reducer;

export const actions = {
  ...slice.actions,
  ...thunks
};
